import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

export const defaultDateFormat = 'yyyy-MM-dd'

interface RentPaidFieldsProps {
  paid: boolean
  onPaidChange: (paid: boolean) => void
  paidDate: string | undefined
  onPaidDateChange: (paidDate: string | undefined) => void
  description: string
  onDescriptionChange: (description: string) => void
  descriptionPlaceholder?: string
  /**
   * Clear and disable the date when `paid` is switched off, so the form can't
   * record an unpaid rent that still carries a payment date.
   */
  clearDateWhenUnpaid?: boolean
}

/**
 * The paid / paid_date / description field block, shared by the single-rent
 * edit modal and the bulk mark-as-paid modal so the two stay in sync.
 * Fully controlled — it holds no state of its own.
 */
export function RentPaidFields({
  paid,
  onPaidChange,
  paidDate,
  onPaidDateChange,
  description,
  onDescriptionChange,
  descriptionPlaceholder,
  clearDateWhenUnpaid = false
}: RentPaidFieldsProps) {
  const { t } = useTranslation('contract')

  const handlePaidChange = (next: boolean) => {
    onPaidChange(next)
    if (clearDateWhenUnpaid && !next) onPaidDateChange(undefined)
  }

  const dateDisabled = clearDateWhenUnpaid && !paid

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <Label className="mb-1.5 block">{t('edit_modal.paid_date')}</Label>
        <DatePicker
          value={paidDate}
          onChange={onPaidDateChange}
          max={format(new Date(), defaultDateFormat)}
          placeholder={t('edit_modal.select_date')}
          disabled={dateDisabled}
        />
      </div>
      <Card
        variant="light"
        className="flex items-end justify-between gap-3 px-4 py-3 sm:items-center"
      >
        <Label>{t('edit_modal.paid')}</Label>
        <Switch checked={paid} onCheckedChange={handlePaidChange} />
      </Card>
      <div className="sm:col-span-2">
        <Label className="mb-1.5 block">{t('edit_modal.description')}</Label>
        <Textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={descriptionPlaceholder}
        />
      </div>
    </div>
  )
}
